import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PrintSalesInvoice from '@/components/print/PrintSalesInvoice';
import { printSalesInvoice } from '@/utils/printDocument';
import { formatINR } from '@/utils/format';
import { salesService } from '@/services/salesService';
import EditDocumentNumberModal from '@/components/common/EditDocumentNumberModal';
import type { InvoicePaymentHistoryRow, SalesInvoiceDetail } from '@/services/salesService';
import type { OutwardGPPrefill } from '@/pages/inventory/gate-pass/outward/page';
import { asEntered } from '@/hooks/useUnits';

interface Props {
  invoice: SalesInvoiceDetail;
  onClose: () => void;
  onCreateChallan?: (invoice: SalesInvoiceDetail) => void;
  /** Called after frontend-only delete */
  onDelete?: (invoiceId: string) => void;
  /** Called when user wants to edit (frontend only — navigate or open form) */
  onEdit?: (invoice: SalesInvoiceDetail) => void;
}

const STATUS_STYLE: Record<string, string> = {
  SAVED:     'bg-emerald-50 text-emerald-700 border-emerald-200',
  DRAFT:     'bg-amber-50 text-amber-700 border-amber-200',
  CANCELLED: 'bg-red-50 text-red-600 border-red-200',
};

const PAYMENT_STATUS_STYLE: Record<string, string> = {
  PAID:    'bg-emerald-50 text-emerald-700 border-emerald-200',
  UNPAID:  'bg-red-50 text-red-600 border-red-200',
  PARTIAL: 'bg-amber-50 text-amber-700 border-amber-200',
};

const PM_LABEL: Record<string, string> = {
  CASH: 'Cash',
  CREDIT: 'Credit',
  CREDIT_AMOUNT: 'Customer Credit',
  CREDIT_NOTE: 'Customer Credit',
  PARTIAL: 'Partial Payment',
  UPI: 'UPI',
  CARD: 'Card',
  CHEQUE: 'Cheque',
  NEFT: 'NEFT',
  RTGS: 'RTGS',
  IMPS: 'IMPS',
  BANK_TRANSFER: 'Bank Transfer',
  DD: 'Demand Draft',
  ONLINE: 'Online Payment',
  WALLET: 'Wallet',
  CRYPTOCURRENCY: 'Cryptocurrency',
};

const getPaymentModeLabel = (mode: string): string => {
  if (!mode) return 'Cash';
  // Try uppercase version first
  const upper = mode.toUpperCase();
  if (PM_LABEL[upper]) return PM_LABEL[upper];
  // Try exact match
  if (PM_LABEL[mode]) return PM_LABEL[mode];
  // Return formatted version
  return mode.charAt(0).toUpperCase() + mode.slice(1).toLowerCase();
};

function amountInWords(amount: number): string {
  const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens  = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  function toWords(n: number): string {
    if (n === 0)       return '';
    if (n < 20)        return units[n];
    if (n < 100)       return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + units[n % 10] : '');
    if (n < 1000)      return units[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + toWords(n % 100) : '');
    if (n < 100000)    return toWords(Math.floor(n / 1000))    + ' Thousand' + (n % 1000   !== 0 ? ' ' + toWords(n % 1000)    : '');
    if (n < 10000000)  return toWords(Math.floor(n / 100000))  + ' Lakh'     + (n % 100000 !== 0 ? ' ' + toWords(n % 100000)  : '');
    return                    toWords(Math.floor(n / 10000000)) + ' Crore'    + (n % 10000000 !== 0 ? ' ' + toWords(n % 10000000) : '');
  }
  const rupees = Math.floor(amount);
  const paise  = Math.round((amount - rupees) * 100);
  let words = 'Rupees ' + (toWords(rupees) || 'Zero');
  if (paise > 0) words += ' and ' + toWords(paise) + ' Paise';
  return words + ' Only';
}

const MODE_COLORS: Record<string, string> = {
  CASH: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  UPI: 'bg-violet-50 text-violet-700 border-violet-100',
  CARD: 'bg-sky-50 text-sky-700 border-sky-100',
  CHEQUE: 'bg-amber-50 text-amber-700 border-amber-100',
  NEFT: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  RTGS: 'bg-rose-50 text-rose-700 border-rose-100',
  CREDIT: 'bg-cyan-50 text-cyan-700 border-cyan-100',
  CREDIT_AMOUNT: 'bg-cyan-50 text-cyan-700 border-cyan-100',
};

const CHEQUE_STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  CLEARED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  BOUNCED: 'bg-red-50 text-red-700 border-red-200',
  INSUFFICIENT_BALANCE: 'bg-red-50 text-red-700 border-red-200',
};
const CHEQUE_STATUS_ICONS: Record<string, string> = {
  PENDING: 'ri-time-line',
  CLEARED: 'ri-checkbox-circle-line',
  BOUNCED: 'ri-close-circle-line',
  INSUFFICIENT_BALANCE: 'ri-error-warning-line',
};
const CHEQUE_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  CLEARED: 'Cleared',
  BOUNCED: 'Bounced',
  INSUFFICIENT_BALANCE: 'Insuff. Balance',
};

export default function InvoiceDetailModal({ invoice, onClose, onCreateChallan, onDelete, onEdit }: Props) {
  const navigate = useNavigate();
  // In InvoiceDetailModal, right after the component starts
  
  // Controls whether payment section shows in modal AND in print
  const [showPayment, setShowPayment]             = useState(true);
  const [printOpen, setPrintOpen]                 = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [paymentHistory, setPaymentHistory]       = useState<InvoicePaymentHistoryRow[]>([]);
  const [selectedReceipt, setSelectedReceipt]     = useState<InvoicePaymentHistoryRow | null>(null);
  const [isEditNumberOpen, setIsEditNumberOpen]   = useState(false);
  const [currentBillNo, setCurrentBillNo]         = useState(invoice.billNo);
  // Lines as shown and printed: in the unit they were entered in (2 Ton @ ₹80,000).
  // invoice.items stays in the stock unit for edit / challan creation.
  const displayItems = invoice.items.map((i) => asEntered(i));

  useEffect(() => {
    setCurrentBillNo(invoice.billNo);
  }, [invoice.billNo]);

  const paymentStatus: string =
    invoice.paymentStatus
    ?? (invoice.paymentMode === 'CASH' ? 'PAID' : invoice.paymentMode === 'PARTIAL' ? 'PARTIAL' : 'UNPAID');

  const normalizedPaymentHistory = paymentHistory
    .filter((row) => Number(row.amount || 0) > 0)
    .map((row) => ({
      id: row.id,
      paymentMode: row.paymentMode,
      amount: Number(row.amount || 0),
      paymentStatus: row.paymentStatus || paymentStatus,
    }));

  const paymentModeBreakdown = Object.values(
    normalizedPaymentHistory.reduce((acc, row) => {
      const key = (row.paymentMode || 'UNKNOWN').toUpperCase();
      if (!acc[key]) {
        acc[key] = {
          id: key,
          paymentMode: key,
          amount: 0,
          paymentStatus: row.paymentStatus,
        };
      }
      acc[key].amount += Number(row.amount || 0);
      if (acc[key].paymentStatus !== row.paymentStatus) {
        acc[key].paymentStatus = 'MIXED';
      }
      return acc;
    }, {} as Record<string, { id: string; paymentMode: string; amount: number; paymentStatus: string }>)
  );

  // Load payment history from backend via typed service
  useEffect(() => {
    let alive = true;

    const loadPaymentHistory = async () => {
      try {
        const rows = await salesService.getInvoicePaymentHistory(invoice.id);
        if (alive) {
          setPaymentHistory(Array.isArray(rows) ? rows : []);
        }
      } catch {
        if (alive) {
          setPaymentHistory([]);
        }
      }
    };

    if (invoice?.id) {
      loadPaymentHistory();
    }

    return () => {
      alive = false;
    };
  }, [invoice?.id]);

   const handleGenerateOutwardGP = (
  invoice: any,
  navigate: any
) => {
  const prefill = {
    partyName: invoice.partyName,
    linkedDocType: 'SALES_INVOICE',
    linkedDocNumber: invoice.id,
    notes: `Generated from Sales Invoice ${invoice.billNo}`,
    items: invoice.items.map((i: any) => ({
      itemName: i.itemName,
      qty: i.qty,
      unit: i.unit || 'Pcs',
    })),   
  };

  navigate('/inventory/gate-pass/outward', {
    state: { prefill },
  });
};
  

  const handleDelete = () => {
    onDelete?.(invoice.id);
    onClose();
  };

  // Tax slab summary
  const taxSlabs = [0, 5, 12, 18, 28].map((rate) => {
    const slabItems = invoice.items.filter((i) => i.taxRate === rate);
    if (!slabItems.length) return null;
    const taxable = slabItems.reduce((s, i) => s + i.taxableAmount, 0);
    const cgst    = slabItems.reduce((s, i) => s + i.cgst, 0);
    const sgst    = slabItems.reduce((s, i) => s + i.sgst, 0);
    const igst    = slabItems.reduce((s, i) => s + i.igst, 0);
    return { rate, taxable, cgst, sgst, igst, total: cgst + sgst + igst };
  }).filter(Boolean) as { rate: number; taxable: number; cgst: number; sgst: number; igst: number; total: number }[];

  const handlePrintReceipt = (payment: InvoicePaymentHistoryRow) => {
    const currentDate = new Date(payment.paymentDate || payment.createdAt || '').toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

    const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Payment Receipt - ${payment.receiptNumber || '—'}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: 'Arial', sans-serif; 
          padding: 40px; 
          color: #1e293b;
          background: #fff;
          max-width: 800px;
          margin: 0 auto;
        }
        .header { 
          text-align: center; 
          border-bottom: 3px solid #4f46e5; 
          padding-bottom: 20px; 
          margin-bottom: 25px;
        }
        .company { font-size: 28px; font-weight: bold; color: #1e293b; }
        .company-sub { font-size: 14px; color: #64748b; margin-top: 4px; }
        .receipt-title { font-size: 18px; color: #4f46e5; font-weight: bold; margin-top: 4px; letter-spacing: 2px; }
        .receipt-no { font-size: 14px; color: #64748b; margin-top: 4px; }
        .details-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 20px;
        }
        .detail-item { display: flex; justify-content: space-between; padding: 4px 0; }
        .detail-label { color: #64748b; font-size: 13px; }
        .detail-value { font-weight: 600; font-size: 13px; color: #1e293b; }
        .amount-box { 
          background: #f0fdf4; 
          border: 2px solid #86efac; 
          border-radius: 12px; 
          padding: 20px; 
          text-align: center; 
          margin: 20px 0;
        }
        .amount-big { font-size: 32px; font-weight: bold; color: #16a34a; }
        .amount-words { font-size: 13px; color: #64748b; margin-top: 6px; font-style: italic; }
        .mode-badge {
          display: inline-block;
          padding: 4px 12px;
          background: #e0e7ff;
          color: #4f46e5;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }
        .footer { margin-top: 40px; display: flex; justify-content: space-between; padding-top: 20px; border-top: 1px solid #e2e8f0; }
        .sign-box { text-align: center; width: 200px; }
        .sign-line { border-top: 1px solid #1e293b; padding-top: 8px; margin-top: 30px; font-size: 12px; color: #64748b; }
        .print-date {
          text-align: center;
          font-size: 11px;
          color: #94a3b8;
          margin-top: 20px;
          border-top: 1px solid #e2e8f0;
          padding-top: 12px;
        }
        @media print { body { padding: 20px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="company">🏢 Digi India</div>
        <div class="company-sub">Inventory Management System</div>
        <div class="receipt-title">PAYMENT RECEIPT</div>
        <div class="receipt-no">Receipt No: <strong>${payment.receiptNumber || '—'}</strong></div>
      </div>

      <div class="details-grid">
        <div class="detail-item">
          <span class="detail-label">Date</span>
          <span class="detail-value">${currentDate}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Payment Mode</span>
          <span class="detail-value"><span class="mode-badge">${payment.paymentMode}</span></span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Customer</span>
          <span class="detail-value">${invoice.partyName}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Invoice No</span>
          <span class="detail-value">${invoice.billNo}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Invoice Amount</span>
          <span class="detail-value">${formatINR(invoice.grandTotal)}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Balance Due</span>
          <span class="detail-value" style="color: ${invoice.balanceDue <= 0 ? '#16a34a' : '#dc2626'};">
            ${formatINR(Math.max(0, invoice.balanceDue))}
          </span>
        </div>
      </div>

      <div class="amount-box">
        <p style="font-size: 12px; color: #15803d; font-weight: 600; text-transform: uppercase; letter-spacing: 1px;">Amount Received</p>
        <p class="amount-big">${formatINR(payment.amount)}</p>
      </div>

      ${
        payment.referenceNo
          ? `
      <div style="margin-bottom: 12px; display: flex; justify-content: space-between; padding: 4px 0; background: #f1f5f9; padding: 8px 16px; border-radius: 6px;">
        <span style="color: #64748b; font-size: 13px;">Reference No</span>
        <span style="font-weight: 600; font-size: 13px; font-family: monospace;">${payment.referenceNo}</span>
      </div>
      `
          : ''
      }

      ${
        payment.paymentMode === 'CHEQUE'
          ? `
      <div style="margin-top: 20px; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; background: #fafafa;">
        <p style="font-size: 12px; font-weight: bold; color: #64748b; text-transform: uppercase; margin-bottom: 10px;">Cheque Details</p>
        <div style="display: flex; justify-content: space-between; margin-bottom: 6px; font-size: 13px;">
          <span style="color: #64748b;">Bank Name</span>
          <span style="font-weight: 600;">${payment.bankName ?? '—'}</span>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 6px; font-size: 13px;">
          <span style="color: #64748b;">Cheque No</span>
          <span style="font-weight: 600; font-family: monospace;">${payment.chequeNo ?? '—'}</span>
        </div>
        <div style="display: flex; justify-content: space-between; font-size: 13px;">
          <span style="color: #64748b;">Cheque Date</span>
          <span style="font-weight: 600;">${payment.chequeDate ?? '—'}</span>
        </div>
      </div>
      `
          : ''
      }

      ${
        payment.notes
          ? `
      <div style="margin-top: 20px; padding: 12px 16px; background: #f8fafc; border-left: 4px solid #94a3b8; border-radius: 4px; font-size: 13px;">
        <strong style="color: #475569;">Notes:</strong> <span style="color: #334155;">${payment.notes}</span>
      </div>
      `
          : ''
      }

      <div class="footer">
        <div class="sign-box">
          <p class="sign-line">Customer Signature</p>
        </div>
        <div class="sign-box">
          <p class="sign-line">Authorized Signatory</p>
        </div>
      </div>

      <div class="print-date">
        Generated on ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
      </div>
    </body>
    </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    }
  };

  const buildPrintData = () => ({
    invoiceNo:        invoice.billNo,
    date:             invoice.date,
    customerName:     invoice.partyName,
    customerGstin:    invoice.customerGstin,
    warehouseName:    invoice.warehouseName,
    warehouseAddress: invoice.warehouseAddress,
    warehouseGstin:   invoice.warehouseGstin,
    companyName:      invoice.companyName,
    companyAddress:   invoice.companyAddress,
    companyGstin:     invoice.companyGstin,
    companyPhone:     invoice.companyPhone,
    billingAddress:   invoice.billingAddress,
    shippingAddress:  invoice.shippingAddress,
    paymentMode:      getPaymentModeLabel(invoice.paymentMode || 'CASH'),
    paymentStatus,
    paidAmount:       invoice.paidAmount ?? 0,
    balanceDue:       invoice.balanceDue ?? 0,
    isSameState:      invoice.isSameState,
    subtotal:         invoice.subtotal,
    totalDiscount:    invoice.totalDiscount,
    taxableAmount:    invoice.taxableAmount,
    cgst:             invoice.totalCGST,
    sgst:             invoice.totalSGST,
    igst:             invoice.totalIGST,
    roundOff:         invoice.roundOff,
    grandTotal:       invoice.grandTotal,
    showPaymentTable: showPayment,   // ← passed to print component
    paymentModes:     paymentModeBreakdown.map((p) => ({
      mode: getPaymentModeLabel(p.paymentMode),
      amount: Number(p.amount || 0),
      status: p.paymentStatus || paymentStatus,
    })),
    remainingDue:     Number(invoice.balanceDue || 0),
    irn:              invoice.irn,
    ackNumber:        invoice.ackNumber,
    ackDate:          invoice.ackDate,
    qrCodeData:       invoice.qrCodeData,
    tdsApplicable:    invoice.tdsApplicable,
    tdsSection:       invoice.tdsSection,
    tdsRate:          invoice.tdsRate,
    tdsAmount:        invoice.tdsAmount,
    expectedReceipt:  invoice.expectedReceipt,
    tcsApplicable:    invoice.tcsApplicable,
    tcsRate:          invoice.tcsRate,
    tcsAmount:        invoice.tcsAmount,
    items: displayItems.map((item, idx) => ({
      sr: idx + 1, name: item.itemName, hsn: item.hsnCode,
      qty: item.qty, unit: item.unit, rate: item.rate,
      discount: item.discount, taxable: item.taxableAmount,
      taxRate: item.taxRate, taxAmt: item.cgst + item.sgst + item.igst,
      total: item.total, cgst: item.cgst, sgst: item.sgst, igst: item.igst,
      batchNumber: (item as any).batchNumber || (item as any).batch_number || undefined,
      mfgDate: (item as any).mfgDate || (item as any).mfg_date || undefined,
      expiryDate: (item as any).expiryDate || (item as any).expiry_date || undefined,
      serials: (Array.isArray((item as any).serials) && (item as any).serials.length > 0)
        ? (item as any).serials
        : (Array.isArray((item as any).serialNumbers) && (item as any).serialNumbers.length > 0)
        ? (item as any).serialNumbers
        : (typeof (item as any).serialNumbers === 'string' && (item as any).serialNumbers.trim())
        ? (item as any).serialNumbers
        : (Array.isArray((item as any).selectedSerials) && (item as any).selectedSerials.length > 0)
        ? (item as any).selectedSerials
        : undefined,
    })),
  });

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl">

          {/* ── HEADER ─────────────────────────────────────────────────────── */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] bg-[#f8fafc] shrink-0">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-[#1e293b]">Tax Invoice</h2>
                <span className="text-lg font-bold text-[#4f46e5] flex items-center gap-1.5">
                  — {currentBillNo}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${PAYMENT_STATUS_STYLE[paymentStatus] ?? STATUS_STYLE.SAVED}`}>
                  {paymentStatus}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${STATUS_STYLE[invoice.status]}`}>
                  {invoice.status}
                </span>
                <span className="text-xs text-slate-500">{invoice.date}</span>
                <span className="text-xs text-slate-400">•</span>
                <span className="text-xs text-slate-500">{getPaymentModeLabel(invoice.paymentMode || 'CASH')}</span>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#e2e8f0] text-slate-500 cursor-pointer">
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* ── BODY ───────────────────────────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">

            {/* ── E-INVOICE / IRN COMPLIANCE BANNER ──────────────────────────────── */}
            {(() => {
              const isB2B = Boolean(invoice.customerGstin && invoice.customerGstin.trim().length === 15);
              const hasIrn = Boolean(invoice.irn || invoice.hasEinvoice || invoice.einvoiceStatus === 'GENERATED');

              if (hasIrn) {
                return (
                  <div className="bg-emerald-50/80 border border-emerald-200 rounded-xl p-3.5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold shrink-0">
                        <i className="ri-qr-code-line text-lg" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider">E-Invoice Registered</span>
                          <span className="text-[11px] px-2 py-0.2 rounded bg-emerald-200/60 text-emerald-900 font-semibold">IRN Generated ✓</span>
                        </div>
                        {invoice.irn && (
                          <div className="text-[11px] font-mono text-emerald-700 mt-0.5 truncate max-w-md">
                            IRN: {invoice.irn}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => navigate('/compliance/e-invoice', { state: { invoiceId: invoice.id, invoiceNumber: invoice.billNo } })}
                      className="px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-white border border-emerald-300 rounded-lg hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      View E-Invoice
                    </button>
                  </div>
                );
              }

              if (isB2B) {
                return (
                  <div className="bg-blue-50/80 border border-blue-200 rounded-xl p-3.5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center text-blue-700 font-bold shrink-0">
                        <i className="ri-file-shield-2-line text-lg" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-blue-900 uppercase tracking-wider">B2B Transaction</span>
                          <span className="text-[11px] px-2 py-0.2 rounded bg-blue-200/60 text-blue-800 font-semibold">IRN Required</span>
                        </div>
                        <p className="text-[11px] text-blue-700 mt-0.5">
                          GSTIN: <span className="font-semibold">{invoice.customerGstin}</span> — E-Invoice registration is required for B2B input tax credit.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        onClose();
                        navigate('/compliance/e-invoice', {
                          state: {
                            autoRegister: true,
                            invoiceId: invoice.id,
                            invoiceNumber: invoice.billNo,
                          },
                        });
                      }}
                      className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap shadow-sm flex items-center gap-1.5"
                    >
                      <i className="ri-send-plane-line" />
                      Register IRN Now
                    </button>
                  </div>
                );
              }

              return (
                <div className="bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 flex items-center justify-between text-xs text-slate-500">
                  <span className="flex items-center gap-2">
                    <i className="ri-user-line text-slate-400" />
                    <span>B2C Transaction — E-Invoice (IRN) is not required for unregistered buyers.</span>
                  </span>
                  <span className="text-[11px] font-medium text-slate-400 bg-white px-2 py-0.5 rounded border border-slate-200">
                    B2C / Exempt
                  </span>
                </div>
              );
            })()}

            {/* Bill To / Ship To / Invoice Details */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-[#f8fafc] rounded-lg p-4 border border-[#e2e8f0]">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Bill To</p>
                <p className="font-semibold text-[#1e293b]">{invoice.partyName}</p>
                {invoice.customerGstin && <p className="text-xs text-slate-500 mt-1">GSTIN: {invoice.customerGstin}</p>}
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{invoice.billingAddress}</p>
              </div>
              <div className="bg-[#f8fafc] rounded-lg p-4 border border-[#e2e8f0]">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Ship To</p>
                <p className="font-semibold text-[#1e293b]">{invoice.partyName}</p>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{invoice.shippingAddress ?? invoice.billingAddress}</p>
              </div>
              <div className="bg-[#f8fafc] rounded-lg p-4 border border-[#e2e8f0]">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Invoice Details</p>
                <div className="grid grid-cols-2 gap-y-2 text-xs">
                  <span className="text-slate-500">Warehouse</span>
                  <span className="font-semibold text-right text-[#1e293b]">{invoice.warehouseName}</span>
                  {invoice.warehouseAddress && (
                    <>
                      <span className="text-slate-500">Warehouse Address</span>
                      <span className="font-semibold text-right text-[#1e293b]">{invoice.warehouseAddress}</span>
                    </>
                  )}
                  {invoice.warehouseGstin && (
                    <>
                      <span className="text-slate-500">Warehouse GSTIN</span>
                      <span className="font-semibold text-right text-[#1e293b]">{invoice.warehouseGstin}</span>
                    </>
                  )}
                  <span className="text-slate-500">Payment Terms</span>
                  <span className="font-semibold text-right text-[#1e293b]">{getPaymentModeLabel(invoice.paymentMode || 'CASH')}</span>
                </div>
              </div>
            </div>

            {/* Items table */}
            <div>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Items</h3>
              <div className="border border-[#e2e8f0] rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      {['#', 'Item', 'Batch', 'HSN', 'Qty', 'Unit', 'Rate', 'Disc%', 'Taxable', 'GST%', 'Tax Amt', 'Total'].map((h, i) => (
                        <th key={h} className={`px-3 py-2.5 text-left font-semibold text-slate-500 border-b border-[#e2e8f0] whitespace-nowrap ${i >= 4 ? 'text-right' : ''}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayItems.map((item, idx) => {
                      const taxAmt = item.cgst + item.sgst + item.igst;
                      const batchNo = (item as any).batchNumber || (item as any).batch_number || "-";
                      const mfgDate = (item as any).mfgDate || (item as any).mfg_date;
                      const itemSerials: any[] = (() => {
                        const s = (item as any).serials || (item as any).serial_items || [];
                        const rawSN = (item as any).serialNumbers || (item as any).serial_numbers;
                        if (Array.isArray(s) && s.length > 0) return s;
                        if (typeof rawSN === 'string' && rawSN.trim()) {
                          return rawSN.split(/[,\n]+/).map((str: string) => str.trim()).filter(Boolean).map((sn: string) => ({ serialNumber: sn }));
                        }
                        if (Array.isArray(rawSN) && rawSN.length > 0) {
                          return rawSN.map((sn: any) => typeof sn === 'string' ? { serialNumber: sn } : sn);
                        }
                        return [];
                      })();

                      return (
                        <tr key={item.id ?? idx} className={`border-b border-slate-50 ${idx % 2 === 1 ? 'bg-[#fafafa]' : ''}`}>
                          <td className="px-3 py-2 text-slate-400 text-center">{idx + 1}</td>
                          <td className="px-3 py-2 font-medium text-[#1e293b]">
                            <div>{item.itemName}</div>
                            {itemSerials.length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {itemSerials.map((s: any, sIdx: number) => (
                                  <span
                                    key={sIdx}
                                    className="px-0 py-0 text-[10px] "
                                    title={s.vendorSerialNumber ? `Vendor Tag: ${s.vendorSerialNumber}` : undefined}
                                  >
                                    
                                    {s.serialNumber || s.serial_number}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 font-mono text-[11px]">
                            {batchNo && (
                              <div>
                                <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 font-semibold">
                                  {batchNo}
                                </span>
                                {mfgDate && (
                                  <div className="text-[10px] text-slate-400 mt-0.5 font-sans">
                                    Mfg: {String(mfgDate).slice(0, 10)}
                                  </div>
                                )}
                              </div>
                            ) 
                            //  : (
                            //   <span className="text-slate-400">—</span>
                            // )
                            }
                          </td>
                          <td className="px-3 py-2 text-slate-500">{item.hsnCode}</td>
                          <td className="px-3 py-2 text-right">
                            {item.qty}
                            {item.stockQty !== undefined && (
                              <div className="text-[10px] text-slate-400">({item.stockQty} {item.stockUnit})</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-500">{item.unit}</td>
                          <td className="px-3 py-2 text-right">{formatINR(item.rate)}</td>
                          <td className="px-3 py-2 text-right">{item.discount > 0 ? `${item.discount}%` : '—'}</td>
                          <td className="px-3 py-2 text-right">{formatINR(item.taxableAmount)}</td>
                          <td className="px-3 py-2 text-right">{item.taxRate}%</td>
                          <td className="px-3 py-2 text-right">{formatINR(taxAmt)}</td>
                          <td className="px-3 py-2 text-right font-semibold text-[#1e293b]">{formatINR(item.total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* GST Summary + Totals */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">GST Summary (Rate-wise)</h3>
                <div className="border border-[#e2e8f0] rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-slate-500 border-b border-[#e2e8f0]">GST Rate</th>
                        <th className="px-3 py-2 text-right font-semibold text-slate-500 border-b border-[#e2e8f0]">Taxable</th>
                        {invoice.isSameState ? (
                          <>
                            <th className="px-3 py-2 text-right font-semibold text-slate-500 border-b border-[#e2e8f0]">CGST</th>
                            <th className="px-3 py-2 text-right font-semibold text-slate-500 border-b border-[#e2e8f0]">SGST</th>
                          </>
                        ) : (
                          <th className="px-3 py-2 text-right font-semibold text-slate-500 border-b border-[#e2e8f0]">IGST</th>
                        )}
                        <th className="px-3 py-2 text-right font-semibold text-slate-500 border-b border-[#e2e8f0]">Total Tax</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taxSlabs.map((slab) => (
                        <tr key={slab.rate} className="border-b border-slate-50">
                          <td className="px-3 py-2 font-medium">{slab.rate}%</td>
                          <td className="px-3 py-2 text-right">{formatINR(slab.taxable)}</td>
                          {invoice.isSameState ? (
                            <>
                              <td className="px-3 py-2 text-right">{formatINR(slab.cgst)}</td>
                              <td className="px-3 py-2 text-right">{formatINR(slab.sgst)}</td>
                            </>
                          ) : (
                            <td className="px-3 py-2 text-right">{formatINR(slab.igst)}</td>
                          )}
                          <td className="px-3 py-2 text-right font-semibold">{formatINR(slab.total)}</td>
                        </tr>
                      ))}
                      {taxSlabs.length === 0 && (
                        <tr><td colSpan={5} className="px-3 py-3 text-center text-slate-400">No tax applicable</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
               {/* Payment Section */}
                {showPayment && paymentHistory.length > 0 && (
                  <div className="mt-3">
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Payment Receipts</h3>
                    <div className="border border-indigo-150 rounded-lg overflow-hidden bg-indigo-50/10">
                      <div className="grid grid-cols-3 px-4 py-2 bg-indigo-100/50 border-b border-indigo-100 text-[11px] font-semibold text-indigo-900">
                        <span>Receipt No</span>
                        <span className="text-center">Mode</span>
                        <span className="text-right">Amount</span>
                      </div>
                      {paymentHistory.map((payment) => (
                        <div
                          key={payment.id}
                          onClick={() => setSelectedReceipt(payment)}
                          className="grid grid-cols-3 px-4 py-2.5 border-b border-indigo-100/30 text-xs last:border-0 items-center hover:bg-indigo-50/60 cursor-pointer transition-colors"
                        >
                          <span className="text-[#4f46e5] font-semibold hover:underline">
                            {payment.receiptNumber || '—'}
                          </span>
                          <span className="text-center text-slate-600 font-medium">
                            {getPaymentModeLabel(payment.paymentMode)}
                          </span>
                          <span className="text-right font-semibold text-slate-900">
                            {formatINR(Number(payment.amount || 0))}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-3 p-3 bg-indigo-50/50 border border-indigo-100 rounded-lg">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Amount in Words</p>
                  <p className="text-xs font-medium text-[#1e293b] italic">{amountInWords(invoice.grandTotal)}</p>
                </div>
              </div>

              <div className="space-y-3">
                {/* Invoice Totals */}
                <div>
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Invoice Totals</h3>
                  <div className="border border-[#e2e8f0] rounded-lg overflow-hidden">
                    {[
                      { label: 'Subtotal',       value: formatINR(invoice.subtotal) },
                      { label: 'Total Discount',  value: invoice.totalDiscount > 0 ? `- ${formatINR(invoice.totalDiscount)}` : '—' },
                      { label: 'Taxable Amount',  value: formatINR(invoice.taxableAmount) },
                      ...(invoice.isSameState
                        ? [{ label: 'CGST', value: formatINR(invoice.totalCGST) }, { label: 'SGST', value: formatINR(invoice.totalSGST) }]
                        : [{ label: 'IGST', value: formatINR(invoice.totalIGST) }]),
                      { label: 'Round Off',       value: invoice.roundOff !== 0 ? formatINR(Math.abs(invoice.roundOff)) : '—' },
                    ].map((row) => (
                      <div key={row.label} className="flex justify-between px-4 py-2.5 border-b border-[#f1f5f9] text-xs last:border-0">
                        <span className="text-slate-500">{row.label}</span>
                        <span className="font-medium text-[#1e293b]">{row.value}</span>
                      </div>
                    ))}
                    <div className="flex justify-between px-4 py-3 bg-[#4f46e5] text-white">
                      <span className="font-bold text-sm">Grand Total</span>
                      <span className="font-bold text-sm">{formatINR(invoice.grandTotal)}</span>
                    </div>
                  </div>

                  {/* TDS reference only — Grand Total above is unchanged */}
                  {invoice.tdsApplicable && (
                    <div className="mt-2 px-1">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold mb-0.5">TDS Info (for reference)</p>
                      <div className="flex justify-between items-center py-0.5">
                        <span className="text-xs text-slate-400 italic">TDS @{invoice.tdsRate}% ({invoice.tdsSection})</span>
                        <span className="text-xs text-slate-400 italic">-{formatINR(invoice.tdsAmount ?? 0)}</span>
                      </div>
                      <div className="flex justify-between items-center py-0.5">
                        <span className="text-xs text-slate-400 italic">Expected Receipt</span>
                        <span className="text-xs text-slate-400 italic">{formatINR(invoice.expectedReceipt ?? invoice.grandTotal)}</span>
                      </div>
                    </div>
                  )}
                </div>

                {showPayment && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Payment Details</h3>
                    <div className="border border-[#e2e8f0] rounded-lg overflow-hidden">
                      {[
                       // { label: 'Payment Mode', value: getPaymentModeLabel(invoice.paymentMode || 'CREDIT') },
                        { label: 'Payment Status', value: paymentStatus },
                        { label: 'Invoice Amount', value: formatINR(invoice.grandTotal ?? 0) },
                        { label: 'Amount Paid', value: formatINR(invoice.paidAmount ?? 0) },
                         { label: 'Balance Due', value: formatINR(invoice.balanceDue ?? 0), red: (invoice.balanceDue ?? 0) > 0 },
                      ].map((row) => (
                        <div key={row.label} className="flex justify-between px-4 py-2.5 border-b border-[#f1f5f9] text-xs last:border-0">
                          <span className="text-slate-500">{row.label}</span>
                          <span className={`font-semibold ${row.red ? 'text-red-600' : 'text-[#1e293b]'}`}>{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>

          {/* ── FOOTER ─────────────────────────────────────────────────────── */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-[#e2e8f0] bg-[#f8fafc] shrink-0">
            {/* Left */}
            <div className="flex items-center gap-3">
              {/* Payment in Print toggle */}
              <button
                type="button"
                onClick={() => setShowPayment((v) => !v)}
                className={`flex items-center gap-1.5 h-7 px-3 rounded-lg border text-xs font-semibold cursor-pointer transition-all select-none ${
                  showPayment
                    ? 'bg-[#4f46e5] text-white border-[#4f46e5]'
                    : 'bg-white text-slate-500 border-[#e2e8f0] hover:border-[#4f46e5] hover:text-[#4f46e5]'
                }`}
                title="Toggle payment details visibility in modal and print"
              >
                <i className={`${showPayment ? 'ri-eye-line' : 'ri-eye-off-line'} text-xs`} />
                Payment in Print
              </button>
            </div>

            {/* Right */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPrintOpen(true)}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#1e293b] hover:bg-[#f1f5f9] cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-printer-line" /> Print Invoice
              </button>

              <button
                onClick={() => handleGenerateOutwardGP(invoice, navigate)}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-file-shield-2-line" /> Outward GP
              </button>

              {invoice.status !== 'CANCELLED' && !invoice.hasChallan && onCreateChallan && (
                <button
                  onClick={() => onCreateChallan(invoice)}
                  className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-sm font-medium hover:bg-amber-100 cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-truck-line" /> Create Challan
                </button>
              )}
              {invoice.hasChallan && (
                <span className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-medium whitespace-nowrap">
                  <i className="ri-checkbox-circle-line" />Challan Created
                </span>
              )}

              {/* Edit — frontend only */}
              {onEdit && (
                <button
                  onClick={() => onEdit(invoice)}
                  title="Edit invoice (API coming soon)"
                  className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#e2e8f0] text-slate-600 hover:bg-slate-50 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-edit-line" /> Edit
                </button>
              )}

              {/* Delete — frontend only */}
              {onDelete && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  title="Delete invoice (API coming soon)"
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-red-200 text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                >
                  <i className="ri-delete-bin-line" />
                </button>
              )}

              <button
                onClick={onClose}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirm */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100 shrink-0">
                <i className="ri-delete-bin-line text-red-600 text-xl" />
              </div>
              <div>
                <p className="text-sm font-bold text-[#1e293b]">Delete Invoice {invoice.billNo}?</p>
                <p className="text-xs text-slate-500 mt-0.5">Removes it from the list. API integration coming soon.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 h-9 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer">Cancel</button>
              <button onClick={handleDelete} className="flex-1 h-9 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 cursor-pointer">Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {selectedReceipt && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2e8f0]">
              <div>
                <h2 className="text-sm font-bold text-[#1e293b]">
                  {selectedReceipt.receiptNumber || '—'}
                </h2>
                <p className="text-xs text-slate-400">
                  {selectedReceipt.paymentDate ? new Date(selectedReceipt.paymentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => handlePrintReceipt(selectedReceipt)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-[#4f46e5] hover:text-indigo-700 cursor-pointer transition-colors"
                  title="Print Receipt"
                >
                  <i className="ri-printer-line text-sm" />
                </button>
                <button
                  onClick={() => setSelectedReceipt(null)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-base" />
                </button>
              </div>
            </div>
            <div className="p-5 space-y-4 text-xs">
              <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-xl p-3.5 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-400 font-medium font-semibold uppercase tracking-wide text-[10px]">Customer</span>
                  <span className="font-semibold text-[#1e293b]">{invoice.partyName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-medium font-semibold uppercase tracking-wide text-[10px]">Invoice No</span>
                  <span className="font-semibold text-[#1e293b]">{invoice.billNo}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-medium font-semibold uppercase tracking-wide text-[10px]">Invoice Amount</span>
                  <span className="font-semibold text-[#1e293b]">{formatINR(invoice.grandTotal)}</span>
                </div>
              </div>

              <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-xl p-3.5 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 font-medium font-semibold uppercase tracking-wide text-[10px]">Payment Mode</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${MODE_COLORS[selectedReceipt.paymentMode] ?? 'bg-slate-50 text-slate-600'}`}>
                    {getPaymentModeLabel(selectedReceipt.paymentMode)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 font-medium font-semibold uppercase tracking-wide text-[10px]">Amount Received</span>
                  <span className="font-bold text-emerald-600 text-sm">{formatINR(selectedReceipt.amount)}</span>
                </div>
                {selectedReceipt.referenceNo && (
                  <div className="flex justify-between">
                    <span className="text-slate-400 font-medium font-semibold uppercase tracking-wide text-[10px]">Reference No</span>
                    <span className="font-mono font-medium text-[#1e293b]">{selectedReceipt.referenceNo}</span>
                  </div>
                )}
                {selectedReceipt.notes && (
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-400 font-medium shrink-0 font-semibold uppercase tracking-wide text-[10px]">Notes</span>
                    <span className="text-[#1e293b] text-right font-medium">{selectedReceipt.notes}</span>
                  </div>
                )}
              </div>

              {selectedReceipt.paymentMode === 'CHEQUE' && (
                <div className="border border-[#e2e8f0] rounded-xl p-3.5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Cheque Details</span>
                    {selectedReceipt.chequeStatus && (
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${CHEQUE_STATUS_COLORS[selectedReceipt.chequeStatus]}`}>
                        <i className={`${CHEQUE_STATUS_ICONS[selectedReceipt.chequeStatus]} text-[10px]`} />
                        {CHEQUE_STATUS_LABELS[selectedReceipt.chequeStatus]}
                      </span>
                    )}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400 font-medium font-semibold uppercase tracking-wide text-[10px]">Bank</span>
                    <span className="font-medium text-[#1e293b]">{selectedReceipt.bankName ?? '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400 font-medium font-semibold uppercase tracking-wide text-[10px]">Cheque No</span>
                    <span className="font-mono font-medium text-[#1e293b]">{selectedReceipt.chequeNo ?? '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400 font-medium font-semibold uppercase tracking-wide text-[10px]">Cheque Date</span>
                    <span className="font-medium text-[#1e293b]">{selectedReceipt.chequeDate ?? '—'}</span>
                  </div>
                  {(selectedReceipt.chequeStatus === 'BOUNCED' || selectedReceipt.chequeStatus === 'INSUFFICIENT_BALANCE') && selectedReceipt.bounceReason && (
                    <div className="pt-2 border-t border-[#e2e8f0]">
                      <p className="text-[10px] text-red-600 font-semibold">Reason: {selectedReceipt.bounceReason}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {printOpen && (
        <PrintSalesInvoice
          data={buildPrintData()}
          onClose={() => setPrintOpen(false)}
          onPrint={() => printSalesInvoice(buildPrintData())}
        />
      )}

      {isEditNumberOpen && (
        <EditDocumentNumberModal
          isOpen={isEditNumberOpen}
          onClose={() => setIsEditNumberOpen(false)}
          onSuccess={(newNum) => {
            setCurrentBillNo(newNum);
            invoice.billNo = newNum;
            invoice.invoiceNo = newNum;
          }}
          moduleKey="SALES_INVOICE"
          id={invoice.id}
          currentNumber={currentBillNo}
        />
      )}
    </>
  );
}
